import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../account/account.service';
import { CreatePatRequest, istSchreibrecht, PatDTO } from '../../models/pat.model';
import { KontoSektionComponent } from '../konto-sektion/konto-sektion.component';
import { TokenAnlegenDialogComponent } from './token-anlegen-dialog/token-anlegen-dialog.component';

@Component({
  selector: 'app-konto-api-tokens',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    DatePipe,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
    KontoSektionComponent,
    TokenAnlegenDialogComponent,
  ],
  templateUrl: './konto-api-tokens.component.html',
  styleUrl: './konto-api-tokens.component.scss',
})
export class KontoApiTokensComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly snackSuccess: MatSnackBarConfig = { panelClass: 'snackbar-success' };
  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error' };

  protected readonly istSchreibrecht = istSchreibrecht;

  protected readonly zustand = signal<'laedt' | 'fehler' | 'geladen'>('laedt');
  protected readonly tokens = signal<PatDTO[]>([]);
  protected readonly dialogOffen = signal(false);
  protected readonly anlegenBusy = signal(false);
  /** Übersetzungsschlüssel der Meldung am Namensfeld. */
  protected readonly nameFehler = signal<string | null>(null);
  protected readonly erzeugt = signal<{ name: string; token: string } | null>(null);
  protected readonly kopiert = signal(false);
  protected readonly kopierFehler = signal(false);
  protected readonly rueckfrage = signal<PatDTO | null>(null);
  protected readonly widerrufenBusy = signal(false);

  private readonly anlegenButton = viewChild.required<ElementRef<HTMLButtonElement>>('anlegenButton');

  ngOnInit(): void {
    this.laden();
  }

  protected laden(): void {
    this.zustand.set('laedt');
    this.accountService
      .listTokens()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokens) => {
          this.tokens.set(tokens.filter((token) => token.revokedAt === null));
          this.zustand.set('geladen');
        },
        error: () => this.zustand.set('fehler'),
      });
  }

  protected scopeLabel(scope: string): string {
    const key = `pat.scope.${scope}`;
    const text = this.translate.instant(key);
    return text === key ? scope : text;
  }

  protected dialogOeffnen(): void {
    this.nameFehler.set(null);
    this.dialogOffen.set(true);
  }

  protected dialogGeschlossen(): void {
    this.dialogOffen.set(false);
    this.fokussieren(() => this.anlegenButton().nativeElement);
  }

  protected anlegen(request: CreatePatRequest): void {
    this.anlegenBusy.set(true);
    this.nameFehler.set(null);
    this.accountService
      .createToken(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (antwort) => {
          this.anlegenBusy.set(false);
          this.tokens.update((tokens) => [antwort.pat, ...tokens]);
          this.erzeugt.set({ name: antwort.pat.name, token: antwort.token });
          this.kopiert.set(false);
          this.kopierFehler.set(false);
          this.dialogGeschlossen();
        },
        error: (fehler: unknown) => {
          this.anlegenBusy.set(false);
          if (fehler instanceof HttpErrorResponse && fehler.status === 409) {
            this.nameFehler.set('pat.createNameExists');
            return;
          }
          this.snackBar.open(this.translate.instant('pat.createError'), undefined, this.snackError);
        },
      });
  }

  protected kopieren(token: string): void {
    this.kopierFehler.set(false);
    const ablage = navigator.clipboard;
    if (!ablage) {
      this.kopierFehler.set(true);
      return;
    }
    ablage.writeText(token).then(
      () => this.kopiert.set(true),
      () => this.kopierFehler.set(true),
    );
  }

  protected widerrufenAnfragen(token: PatDTO): void {
    this.rueckfrage.set(token);
    this.fokussieren(() => this.host.nativeElement.querySelector<HTMLElement>('[data-testid="rueckfrage-abbrechen"]'));
  }

  protected rueckfrageAbbrechen(): void {
    const token = this.rueckfrage();
    if (!token || this.widerrufenBusy()) return;
    this.rueckfrage.set(null);
    this.papierkorbFokussieren(token.id);
  }

  protected widerrufen(): void {
    const token = this.rueckfrage();
    if (!token || this.widerrufenBusy()) return;
    this.widerrufenBusy.set(true);
    this.accountService
      .revokeToken(token.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.widerrufenBusy.set(false);
          this.rueckfrage.set(null);
          this.tokens.update((tokens) => tokens.filter((t) => t.id !== token.id));
          this.snackBar.open(this.translate.instant('pat.revokeSuccess'), undefined, this.snackSuccess);
          this.fokussieren(() => this.anlegenButton().nativeElement);
        },
        error: (fehler: unknown) => {
          this.widerrufenBusy.set(false);
          this.rueckfrage.set(null);
          if (fehler instanceof HttpErrorResponse && fehler.status === 404) {
            this.laden();
            this.fokussieren(() => this.anlegenButton().nativeElement);
            return;
          }
          this.snackBar.open(this.translate.instant('pat.revokeError'), undefined, this.snackError);
          this.papierkorbFokussieren(token.id);
        },
      });
  }

  private papierkorbFokussieren(id: string): void {
    this.fokussieren(() =>
      this.host.nativeElement.querySelector<HTMLElement>(`[data-token-id="${id}"] [data-testid="token-widerrufen"]`),
    );
  }

  private fokussieren(ziel: () => HTMLElement | null): void {
    afterNextRender(() => ziel()?.focus(), { injector: this.injector });
  }
}
